/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import * as _ from 'lodash';
import template from './workspace-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {
  addWorkspaceWidget,
  buildWorkspaceGridsterOpts,
  initWorkspaceModel,
  prepareWorkspaceWidgetsForSubmit,
  removeWorkspaceWidget,
  setupWorkspaceWidgetDefaults,
  workspaceWhichFields
} from './workspace-commons';
import type {WorkspaceGridsterResource} from './workspace-gridster-item.component';
import {WorkspaceWidgetModalComponent} from './workspace-widget-modal.component';
import {TeamApiService} from '../admin/team/team-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {WorkspaceRegistryService} from '../../components/workspace-registry.service';
import {StateService} from '@uirouter/core';
import {WorkspaceApiService, WorkspaceModel} from './workspace-api.service';
import {deferSignalStateWrite} from '../common/util/template-state';
import {APP_SHARED} from '../common/app-shared';
import {FORM_CONTROLS} from '../common/directives/form-controls/form-controls';
import {WorkspaceEditWorkspaceComponent} from './workspace-edit-workspace.component';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, ButtonsModule, ...FORM_CONTROLS, WorkspaceEditWorkspaceComponent],
  selector: 'adn-workspace-edit-page',
  template
})
export class WorkspaceEditPageComponent extends SignalAdminResourceEditPageBase<WorkspaceModel> {
  readonly hasNetworkPermission = signal(false);
  readonly allTeams = signal<any[] | null>(null);
  readonly widgets = signal<any[]>([]);
  readonly gridsterOpts = signal(buildWorkspaceGridsterOpts(true));

  readonly gridsterResource: WorkspaceGridsterResource = {
    deleteWidget: (widget) => this.deleteWidget(widget),
    changeType: (widget) => this.changeType(widget),
    whichFields: (fieldType, widget) => this.whichFields(fieldType, widget),
    addComparisons: (widget) => this.addComparisons(widget),
    removeComparisons: (widget, index) => this.removeComparisons(widget, index),
    markDirty: () => this.markExternalFormDirty()
  };

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    @Inject(WorkspaceRegistryService) private readonly WorkspaceRegistry: any,
    private readonly $state: StateService,
    private readonly translate: TranslateService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly angularModalService: AngularModalService
  ) {
    super(inject(WorkspaceApiService), 'workspace');
  }

  compareTeams(a: any, b: any): boolean {
    return a?.id === b?.id;
  }

  protected override afterEditingModelReset(): void {
    this.hasNetworkPermission.set(this.LocalUserPermissions.hasNetworkPermission('MANAGE_NETWORK_DASHBOARDS'));
    initWorkspaceModel(
      this.editingModel,
      this.hasNetworkPermission(),
      this.LocalNetworkProfile.isPureMarketplacePlatform()
    );
    if (this.editingModel.type === 'TEAM') {
      this.loadTeams();
    }
    this.setupWidgetDefaults();
    this.widgets.set(this.editingModel.data.widgets);
    this.gridsterOpts.set(buildWorkspaceGridsterOpts(true, () => this.markExternalFormDirty()));
  }

  protected override prepareForSubmit(model: WorkspaceModel): void {
    prepareWorkspaceWidgetsForSubmit(
      model.data.widgets,
      this.adnTimeShifter
    );
  }

  protected override afterSave(model: WorkspaceModel): void {
    this.WorkspaceRegistry.update(model);
    setTimeout(() => {
      // Reload: the parent view state keeps its pre-edit resolves otherwise.
      this.$state.go('app.wor.workspaces.workspace', {id: model.id}, {reload: 'app.wor.workspaces.workspace'});
    }, 0);
  }

  updateType(): void {
    if (this.editingModel.type === 'TEAM') {
      this.loadTeams();
    } else {
      this.editingModel.team = undefined;
    }
    this.markExternalFormDirty();
  }

  changeType(widget: any): void {
    widget.objIds = [];
    this.markExternalFormDirty();
  }

  whichFields(fieldType: string, widget: any): boolean {
    return workspaceWhichFields(fieldType, widget);
  }

  addComparisons(widget: any): void {
    widget.fieldComparisons = widget.fieldComparisons || [];
    widget.fieldComparisons.push({});
    this.markExternalFormDirty();
  }

  removeComparisons(widget: any, index: number): void {
    _.pullAt(widget.fieldComparisons, index);
    this.markExternalFormDirty();
  }

  selectWidgets(): void {
    const modalRef = this.angularModalService.show(WorkspaceWidgetModalComponent, {
      class: 'modal-lg adnDialog'
    });
    (modalRef.content as WorkspaceWidgetModalComponent).onSaved.subscribe((selections: any[]) => {
      if (!selections?.length) {
        return;
      }
      const widgets = [...this.widgets()];
      const added = _.map(selections, (sel) => addWorkspaceWidget(widgets, sel));
      // Only hydrate the new tiles — re-running defaults on every existing widget
      // was a multi-frame pause before paint.
      setupWorkspaceWidgetDefaults(
        added,
        (key) => this.translate.instant(key),
        this.LocalNetworkProfile,
        this.adnTimeShifter
      );
      this.editingModel.data.widgets = widgets;
      this.widgets.set(widgets);
      this.markExternalFormDirty();
    });
  }

  deleteWidget(widget: any): void {
    const widgets = [...this.widgets()];
    removeWorkspaceWidget(widgets, widget);
    this.editingModel.data.widgets = widgets;
    this.widgets.set(widgets);
    this.markExternalFormDirty();
  }

  private setupWidgetDefaults(): void {
    setupWorkspaceWidgetDefaults(
      this.editingModel.data.widgets,
      (key) => this.translate.instant(key),
      this.LocalNetworkProfile,
      this.adnTimeShifter
    );
  }

  private loadTeams(): void {
    this.allTeams.set(null);
    const perms = this.LocalUserPermissions.getAllVisPermissions();
    const teamTypes: string[] = [];
    if (perms.AX_ADVERTISER_ADMIN === true || perms.AX_ADVERTISER_TEAM_ADMIN === true) {
      teamTypes.push('AX_ADVERTISER');
    }
    if (perms.AX_PUBLISHER_ADMIN === true || perms.AX_PUBLISHER_TEAM_ADMIN === true) {
      teamTypes.push('AX_PUBLISHER');
    }
    const params = {minimal: true, teamTypes: teamTypes.join(';')};
    this.teamApi.listOptions(params).subscribe((page) => {
      deferSignalStateWrite(() => this.allTeams.set(page.results || []));
    });
  }
}
